import { AssignmentTurnedIn, Delete, FilePresent } from '@mui/icons-material';
import { Box, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { DateInput } from 'lib/common/Inputs/DateInput';
import { DecimalInput } from 'lib/common/Inputs/DecimalInput';
import { IntegerInput } from 'lib/common/Inputs/IntegerInput';
import { MoneyInput } from 'lib/common/Inputs/MoneyInput';
import AccordionCard from 'lib/designSystem/AccordionCard';
import CustomButton from 'lib/designSystem/Button';
import SmallHeaderWithIcon from 'lib/designSystem/SmallHeaderWithIcon';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { calculateDays, isWorkingDay } from 'lib/functions/dates';
import { formatMoney, getShortString, showAlert } from 'lib/functions/utils';
import React from 'react';
import { useDispatch } from 'react-redux';

import { ResellDocument } from '../../types';
import CessionStatusLight, {
  CessionStatusData,
} from './components/CessionStatusLight';
import OperationChip from './components/OperationChip';
import SortButton, {
  getDefaultSortField,
  getDefaultSortOrder,
  SortField,
  SortOrder,
  sortResells,
} from './components/SortButton';

interface HeaderInfoItemProps {
  label: string;
  value: React.ReactNode;
  fontWeight?: number;
  valueMaxLength?: number;
}

const HeaderInfoItem: React.FC<HeaderInfoItemProps> = ({
  label,
  value,
  fontWeight = 500,
  valueMaxLength,
}) => {
  const renderValue = () => {
    if (valueMaxLength && typeof value === 'string' && value) {
      const truncatedValue = getShortString(value, valueMaxLength);
      const needsTooltip = value.length > valueMaxLength;

      if (needsTooltip) {
        return (
          <LightTooltip
            title={<Typography sx={{ padding: 1 }}>{value}</Typography>}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {truncatedValue}
            </Typography>
          </LightTooltip>
        );
      }
    }

    if (typeof value === 'string') {
      return (
        <Typography variant="body2" sx={{ fontWeight }}>
          {value}
        </Typography>
      );
    }

    return value;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontSize: '0.7rem', mb: 0.5 }}
      >
        {label}
      </Typography>
      {renderValue()}
    </Box>
  );
};

interface ResellsTableProps {
  resells: ResellDocument[];
  setResells: (resells: ResellDocument[]) => void;
  canForcePaymentDate: boolean;
  readOnly?: boolean;
  hasCessionIntegration?: boolean;
  markResellAsModified?: (resellId: string) => void;
  isNomineeView?: boolean;
  setOpenDocumentSelector: (open: boolean) => void;
  setOpenDocumentBatchImporter: (open: boolean) => void;
  loadingSuggestions?: boolean;
}

const ResellsTable = (props: ResellsTableProps) => {
  const {
    resells,
    setResells,
    canForcePaymentDate,
    readOnly = false,
    hasCessionIntegration = false,
    markResellAsModified,
    isNomineeView = false,
    setOpenDocumentSelector,
    setOpenDocumentBatchImporter,
    loadingSuggestions = false,
  } = props;
  const dispatch = useDispatch();
  const [expandedResells, setExpandedResells] = React.useState<Set<string>>(
    new Set(),
  );
  const [sortBy, setSortBy] = React.useState<SortField>(getDefaultSortField());
  const [sortOrder, setSortOrder] = React.useState<SortOrder>(
    getDefaultSortOrder(),
  );

  const handleAccordionChange =
    (resellId: string) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedResells((prev) => {
        const newSet = new Set(prev);
        if (isExpanded) {
          newSet.add(resellId);
        } else {
          newSet.delete(resellId);
        }
        return newSet;
      });
    };

  const dateValidators = (date: Date, type: string, resellId: string) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return false;

    if (canForcePaymentDate) {
      const days = calculateDays(
        type === 'startDate' ? date : new Date(resell.startDate),
        type === 'endDate' ? date : new Date(resell.endDate),
      );
      if (days <= 0) {
        showAlert(
          dispatch,
          'warning',
          'La fecha de vencimiento no puede ser menor a la fecha de operación de venta a fondo.',
        );
        returnTempDateToActualDate(resellId);
        return false;
      }
      return days;
    }
    if (!isWorkingDay(date) && type === 'endDate') {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento seleccionada no es un día hábil.',
      );
    }
    const days = calculateDays(
      type === 'startDate' ? date : new Date(resell.startDate),
      type === 'endDate' ? date : new Date(resell.endDate),
    );
    if (days <= 0) {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento no puede ser menor a la fecha de liberación',
      );
      returnTempDateToActualDate(resellId);
      return false;
    }
    return days;
  };

  const returnTempDateToActualDate = (resellId: string) => {
    const newResells = resells.map((doc: ResellDocument) => {
      if (doc.id === resellId) {
        // Convertir endDate a string YYYY-MM-DD si es Date
        let endDateStr = '';
        if (typeof doc.endDate === 'string') {
          endDateStr = doc.endDate;
        } else if (doc.endDate instanceof Date) {
          const year = doc.endDate.getFullYear();
          const month = String(doc.endDate.getMonth() + 1).padStart(2, '0');
          const day = String(doc.endDate.getDate()).padStart(2, '0');
          endDateStr = `${year}-${month}-${day}`;
        }
        return {
          ...doc,
          tempEndDate: endDateStr,
        };
      }
      return doc;
    });
    setResells(newResells);
  };

  const handleChangeTempDate = (value: string, resellId: string) => {
    const newResells = resells.map((doc: ResellDocument) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          tempEndDate: value,
        };
      }
      return doc;
    });
    setResells(newResells);
  };

  const handleChangeDate = (value: string, type: string, resellId: string) => {
    if (!value) return;
    const days = dateValidators(new Date(value), type, resellId);
    if (!days) return;
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;
    const newResells = resells.map((doc: ResellDocument) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          [type]: value,
          // Si se actualiza endDate, también actualizar tempEndDate
          ...(type === 'endDate' && { tempEndDate: value }),
          days,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const handleChangeEndDate = (value: string, resellId: string) => {
    handleChangeTempDate(value, resellId);
    if (value) {
      handleChangeDate(value, 'endDate', resellId);
    }
  };

  const handleChangeCessionDate = (value: string, resellId: string) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;
    const newResells = resells.map((doc: ResellDocument) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          cessionDate: value || null,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const handleChangeAmount = (value: number | null, resellId: string) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;

    const valueToCalculate = value ?? 0;
    if (valueToCalculate > resell.originalAmount) {
      return showAlert(
        dispatch,
        'warning',
        'El monto a financiar no puede ser mayor al monto del documento',
      );
    }

    const newResells = resells.map((doc: ResellDocument) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          amount: valueToCalculate,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const handleChangeRate = (value: number | null, resellId: string) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;

    const valueToCalculate = value ?? 0;
    if (valueToCalculate > 100 || valueToCalculate < 0) return;

    const newResells = resells.map((doc: ResellDocument) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          rate: valueToCalculate,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const handleChangeAdvance = (value: number | null, resellId: string) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;

    const valueToCalculate = value ?? 0;
    if (valueToCalculate > 100 || valueToCalculate < 0) return;

    const newResells = resells.map((doc: ResellDocument) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          advance: valueToCalculate,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const handleChangeArrearRate = (value: number | null, resellId: string) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;

    const valueToCalculate = value ?? 0;
    if (valueToCalculate > 100 || valueToCalculate < 0) return;

    const newResells = resells.map((doc: ResellDocument) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          arrearRate: valueToCalculate,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const handleChangeOperationCostFixed = (
    value: number | null,
    resellId: string,
  ) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;

    const numValue = value ?? 0;
    if (numValue < 0) return;

    const newResells = resells.map((doc: ResellDocument) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          operationCostFixed: numValue,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const assignConditionsForAllResells = (sourceId: string) => {
    const sourceResell = resells.find((r) => r.id === sourceId);
    if (!sourceResell) return;

    // Convertir endDate a string YYYY-MM-DD si es Date para tempEndDate
    let tempEndDateStr = '';
    if (typeof sourceResell.endDate === 'string') {
      tempEndDateStr = sourceResell.endDate;
    } else if (sourceResell.endDate instanceof Date) {
      const year = sourceResell.endDate.getFullYear();
      const month = String(sourceResell.endDate.getMonth() + 1).padStart(
        2,
        '0',
      );
      const day = String(sourceResell.endDate.getDate()).padStart(2, '0');
      tempEndDateStr = `${year}-${month}-${day}`;
    }

    const newResells = resells.map((doc: ResellDocument) => {
      if (doc.id === sourceId) {
        return doc;
      }
      return {
        ...doc,
        rate: sourceResell.rate,
        arrearRate: sourceResell.arrearRate,
        arrearsRate: sourceResell.arrearsRate,
        arrearTableId: sourceResell.arrearTableId,
        advance: sourceResell.advance,
        startDate: sourceResell.startDate,
        endDate: sourceResell.endDate,
        tempEndDate: tempEndDateStr,
        operationCostFixed: sourceResell.operationCostFixed,
        operationCostVariable: sourceResell.operationCostVariable,
        fundId: sourceResell.fundId,
        fund: sourceResell.fund,
        days: sourceResell.days,
      };
    });

    setResells(newResells);
  };

  const assignRateForAllResells = (sourceId: string) => {
    const sourceResell = resells.find((r) => r.id === sourceId);
    if (!sourceResell) return;

    const newResells = resells.map((doc: ResellDocument) => {
      if (doc.id === sourceId) {
        return doc;
      }
      return {
        ...doc,
        rate: sourceResell.rate,
      };
    });

    setResells(newResells);
  };

  const assignEndDateForAllResells = (sourceId: string) => {
    const sourceResell = resells.find((r) => r.id === sourceId);
    if (!sourceResell) return;

    // Convertir endDate a string YYYY-MM-DD si es Date para tempEndDate
    let tempEndDateStr = '';
    if (typeof sourceResell.endDate === 'string') {
      tempEndDateStr = sourceResell.endDate;
    } else if (sourceResell.endDate instanceof Date) {
      const year = sourceResell.endDate.getFullYear();
      const month = String(sourceResell.endDate.getMonth() + 1).padStart(
        2,
        '0',
      );
      const day = String(sourceResell.endDate.getDate()).padStart(2, '0');
      tempEndDateStr = `${year}-${month}-${day}`;
    } else if (typeof sourceResell.tempEndDate === 'string') {
      tempEndDateStr = sourceResell.tempEndDate;
    }

    const newResells = resells.map((doc: ResellDocument) => {
      if (doc.id === sourceId) {
        return doc;
      }
      return {
        ...doc,
        endDate: sourceResell.endDate,
        tempEndDate: tempEndDateStr,
      };
    });

    setResells(newResells);

    newResells.forEach((doc) => {
      if (doc.id !== sourceId && markResellAsModified) {
        markResellAsModified(doc.id);
      }
    });
  };

  const deleteRow = (resellId: string) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;

    const newInvoices = resells.map((doc: ResellDocument) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          deleted: true,
        };
      }
      return doc;
    });
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
    setResells(newInvoices);
  };

  const activeResells = resells.filter((row: ResellDocument) => !row.deleted);
  const hasResells = activeResells.length > 0;

  // Ordenar las facturas usando la utilidad del SortButton
  const sortedResells = React.useMemo(() => {
    return sortResells(activeResells, sortBy, sortOrder);
  }, [activeResells, sortBy, sortOrder]);

  return (
    <>
      <Box
        sx={{
          padding: '10px 0px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <SmallHeaderWithIcon
          title={`Documentos (${activeResells.length})`}
          icon={<FilePresent fontSize="small" />}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasResells && (
            <SortButton
              selectedValue={sortBy}
              selectedOrder={sortOrder}
              onSelect={setSortBy}
              onOrderChange={setSortOrder}
            />
          )}
          {!isNomineeView && (
            <>
              <CustomButton
                onClick={() => setOpenDocumentBatchImporter(true)}
                color="secondary"
              >
                Importar
              </CustomButton>
              <CustomButton
                onClick={() => setOpenDocumentSelector(true)}
                color="primary"
                disabled={loadingSuggestions}
                loading={loadingSuggestions}
              >
                Agregar
              </CustomButton>
            </>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxHeight: '600px',
          overflowY: 'auto',
          pr: 1,
        }}
      >
        {!hasResells ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: 'grey.50',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'grey.300',
            }}
          >
            <Typography
              variant="h6"
              component="h3"
              gutterBottom
              sx={{ color: 'text.primary', fontWeight: 500, mb: 1 }}
            >
              No hay facturas agregadas
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 500, mx: 'auto' }}
            >
              Agrega facturas para crear nómina y ceder
            </Typography>
          </Paper>
        ) : (
          sortedResells.map((row: ResellDocument) => {
            const financedAmount = (row.amount * row.advance) / 100 || 0;
            const isExpanded = expandedResells.has(row.id);

            return (
              <AccordionCard
                key={row.id}
                id={`resell-${row.id}`}
                size="small"
                enabledEffects={true}
                expanded={isExpanded}
                onChange={handleAccordionChange(row.id)}
                summary={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      py: 1,
                      px: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Box sx={{ width: '50px', flexShrink: 0 }}>
                        <HeaderInfoItem
                          label="FOLIO"
                          value={row.folio}
                          fontWeight={600}
                        />
                      </Box>

                      <Box
                        sx={{
                          width: '180px',
                          flexShrink: 1,
                          minWidth: '100px',
                        }}
                      >
                        <HeaderInfoItem
                          label="CLIENTE"
                          value={row.businessName}
                          fontWeight={500}
                          valueMaxLength={20}
                        />
                      </Box>

                      <Box
                        sx={{
                          width: '180px',
                          flexShrink: 1,
                          minWidth: '100px',
                        }}
                      >
                        <HeaderInfoItem
                          label="DEUDOR"
                          value={row.stakeholderName}
                          fontWeight={500}
                          valueMaxLength={20}
                        />
                      </Box>

                      <Box sx={{ width: '90px', flexShrink: 0 }}>
                        <HeaderInfoItem
                          label="MONTO DOC."
                          value={formatMoney(row.originalAmount)}
                          fontWeight={500}
                        />
                      </Box>

                      <Box sx={{ width: '60px', flexShrink: 0 }}>
                        <HeaderInfoItem
                          label="ANTICIPO"
                          value={`${row.advance ?? 0}%`}
                          fontWeight={500}
                        />
                      </Box>

                      <Box sx={{ width: '60px', flexShrink: 0 }}>
                        <HeaderInfoItem
                          label="TASA"
                          value={`${row.rate ?? 0}%`}
                          fontWeight={500}
                        />
                      </Box>

                      <Box sx={{ width: '140px', flexShrink: 0 }}>
                        <HeaderInfoItem
                          label="MONTO FINANCIADO"
                          value={formatMoney(financedAmount)}
                          fontWeight={500}
                        />
                      </Box>

                      <Box sx={{ width: '90px', flexShrink: 0 }}>
                        <HeaderInfoItem
                          label="DIF. PRECIO"
                          value={formatMoney(row.rateCost || 0)}
                          fontWeight={500}
                        />
                      </Box>

                      <Box sx={{ width: '50px', flexShrink: 0 }}>
                        <HeaderInfoItem
                          label="DÍAS"
                          value={`${row.days ?? 0}`}
                          fontWeight={500}
                        />
                      </Box>

                      <OperationChip
                        requestId={row.requestId}
                        requestType={row.requestType}
                      />
                    </Box>

                    {readOnly && hasCessionIntegration ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          ml: 2,
                          mr: 4,
                          flexShrink: 0,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <CessionStatusLight
                          status={row.cessionStatus as CessionStatusData}
                        />
                      </Box>
                    ) : !readOnly ? (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          ml: 2,
                          mr: 4,
                          flexShrink: 0,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <LightTooltip
                          placement="top"
                          title={
                            <Typography sx={{ padding: 1 }}>
                              Eliminar factura
                            </Typography>
                          }
                        >
                          <IconButton
                            onClick={() => deleteRow(row.id)}
                            size="small"
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: 'error.light',
                                color: 'error.dark',
                              },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </LightTooltip>
                        <LightTooltip
                          placement="top"
                          title={
                            <Typography sx={{ padding: 1 }}>
                              Asignar estas condiciones a todos los documentos
                            </Typography>
                          }
                        >
                          <IconButton
                            onClick={() =>
                              assignConditionsForAllResells(row.id)
                            }
                            size="small"
                            sx={{
                              color: 'success.main',
                              '&:hover': {
                                backgroundColor: 'success.light',
                                color: 'success.dark',
                              },
                            }}
                          >
                            <AssignmentTurnedIn fontSize="small" />
                          </IconButton>
                        </LightTooltip>
                      </Box>
                    ) : null}
                  </Box>
                }
              >
                <Grid container spacing={3} sx={{ py: 2, px: 2 }}>
                  <Grid item xs={12} md={4}>
                    <Stack spacing={2.5} sx={{ px: 3 }}>
                      <DateInput
                        label="Fecha Vencimiento Documento"
                        value={row.paymentDate}
                        onChange={() => {}}
                        disabled={true}
                      />
                      <DateInput
                        label="Fecha de Cesión"
                        value={row.cessionDate as string}
                        onChange={(value) =>
                          handleChangeCessionDate(value, row.id)
                        }
                        disabled={readOnly}
                      />
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <DateInput
                          label="Fecha Vencimiento Operación"
                          value={
                            typeof row.tempEndDate === 'string'
                              ? row.tempEndDate
                              : row.tempEndDate instanceof Date
                                ? `${row.tempEndDate.getFullYear()}-${String(
                                    row.tempEndDate.getMonth() + 1,
                                  ).padStart(2, '0')}-${String(
                                    row.tempEndDate.getDate(),
                                  ).padStart(2, '0')}`
                                : ''
                          }
                          onChange={(value) =>
                            handleChangeEndDate(value, row.id)
                          }
                          disabled={readOnly}
                          fullWidth
                        />
                        {!readOnly && (
                          <LightTooltip
                            placement="top"
                            title={
                              <Typography sx={{ padding: 1 }}>
                                Asignar esta fecha de vencimiento a todos los
                                documentos
                              </Typography>
                            }
                          >
                            <IconButton
                              onClick={() => assignEndDateForAllResells(row.id)}
                              size="small"
                              sx={{
                                color: 'success.main',
                                '&:hover': {
                                  backgroundColor: 'success.light',
                                  color: 'success.dark',
                                },
                              }}
                            >
                              <AssignmentTurnedIn fontSize="small" />
                            </IconButton>
                          </LightTooltip>
                        )}
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Stack spacing={2.5} sx={{ px: 3 }}>
                      <MoneyInput
                        label="Monto"
                        value={row.amount ?? 0}
                        onChange={(value) => handleChangeAmount(value, row.id)}
                        disabled={readOnly}
                        fullWidth
                        max={row.originalAmount}
                      />
                      <IntegerInput
                        label="Anticipo"
                        value={row.advance ?? 0}
                        onChange={(value) => handleChangeAdvance(value, row.id)}
                        disabled={readOnly}
                        fullWidth
                        min={0}
                        max={100}
                      />
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <DecimalInput
                          label="Tasa"
                          value={row.rate ?? 0}
                          onChange={(value) => handleChangeRate(value, row.id)}
                          disabled={readOnly}
                          fullWidth
                          isPercentage={true}
                          min={0}
                          max={100}
                        />
                        {!readOnly && (
                          <LightTooltip
                            placement="top"
                            title={
                              <Typography sx={{ padding: 1 }}>
                                Asignar esta tasa a todos los documentos
                              </Typography>
                            }
                          >
                            <IconButton
                              onClick={() => assignRateForAllResells(row.id)}
                              size="small"
                              sx={{
                                color: 'success.main',
                                '&:hover': {
                                  backgroundColor: 'success.light',
                                  color: 'success.dark',
                                },
                              }}
                            >
                              <AssignmentTurnedIn fontSize="small" />
                            </IconButton>
                          </LightTooltip>
                        )}
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Stack spacing={2.5} sx={{ px: 3 }}>
                      <DecimalInput
                        label="Tasa Mora"
                        value={row.arrearRate ?? 0}
                        onChange={(value) =>
                          handleChangeArrearRate(value, row.id)
                        }
                        disabled={readOnly}
                        fullWidth
                        isPercentage={true}
                        min={0}
                        max={100}
                      />
                      <MoneyInput
                        label="Comisión"
                        value={row.operationCostFixed ?? 0}
                        onChange={(value) =>
                          handleChangeOperationCostFixed(value, row.id)
                        }
                        disabled={readOnly}
                        fullWidth
                        min={0}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </AccordionCard>
            );
          })
        )}
      </Box>
    </>
  );
};

export default ResellsTable;
